import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class FISDataCategory extends BaseObj {
    public ID: number;
    public __id: number;

    public Codex: number | null;
    public __codex: number | null;

    public Finalecodex: number | null;
    public __finalecodex: number | null;

    public Fiskategori: string | null;
    public __fiskategori: string | null;

    public Fistype: string | null;
    public __fistype: string | null;

    public Fvalue: number | null;
    public __fvalue: number | null;

    public Minpenalty: number | null;
    public __minpenalty: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Codex?: number | null;
        EntityState?: EntityState;
        Finalecodex?: number | null;
        Fiskategori?: string | null;
        Fistype?: string | null;
        Fvalue?: number | null;
        Minpenalty?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<FISDataCategory>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('FISDataCategory tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Codex = values.Codex ?? null;
        this.__codex = values.Codex ?? null;
        Object.defineProperty(this, 'Codex', {
            get(): number | null {
                return this.__codex;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Codex', this.__codex, value);
                this.__codex = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Finalecodex = values.Finalecodex ?? null;
        this.__finalecodex = values.Finalecodex ?? null;
        Object.defineProperty(this, 'Finalecodex', {
            get(): number | null {
                return this.__finalecodex;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Finalecodex', this.__finalecodex, value);
                this.__finalecodex = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Fiskategori = values.Fiskategori ?? null;
        this.__fiskategori = values.Fiskategori ?? null;
        Object.defineProperty(this, 'Fiskategori', {
            get(): string | null {
                return this.__fiskategori;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Fiskategori', this.__fiskategori, value);
                this.__fiskategori = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Fistype = values.Fistype ?? null;
        this.__fistype = values.Fistype ?? null;
        Object.defineProperty(this, 'Fistype', {
            get(): string | null {
                return this.__fistype;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Fistype', this.__fistype, value);
                this.__fistype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Fvalue = values.Fvalue ?? null;
        this.__fvalue = values.Fvalue ?? null;
        Object.defineProperty(this, 'Fvalue', {
            get(): number | null {
                return this.__fvalue;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Fvalue', this.__fvalue, value);
                this.__fvalue = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Minpenalty = values.Minpenalty ?? null;
        this.__minpenalty = values.Minpenalty ?? null;
        Object.defineProperty(this, 'Minpenalty', {
            get(): number | null {
                return this.__minpenalty;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Minpenalty', this.__minpenalty, value);
                this.__minpenalty = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Codex": ${JSON.stringify(this.Codex)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"Finalecodex": ${JSON.stringify(this.Finalecodex)}` +
            ',' +
            `"Fiskategori": ${JSON.stringify(this.Fiskategori)}` +
            ',' +
            `"Fistype": ${JSON.stringify(this.Fistype)}` +
            ',' +
            `"Fvalue": ${JSON.stringify(this.Fvalue)}` +
            ',' +
            `"Minpenalty": ${JSON.stringify(this.Minpenalty)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of FISDataCategory from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Codex?: number | null;
        Finalecodex?: number | null;
        Fiskategori?: string | null;
        Fistype?: string | null;
        Fvalue?: number | null;
        Minpenalty?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): FISDataCategory {
        return new FISDataCategory(values);
    }

    public static _FromServerJson(json: any): FISDataCategory {
        return new FISDataCategory({
            ID: json.ID,
            Codex: json.Codex ?? null,
            Finalecodex: json.Finalecodex ?? null,
            Fiskategori: json.Fiskategori ?? '',
            Fistype: json.Fistype ?? '',
            Fvalue: json.Fvalue ?? null,
            Minpenalty: json.Minpenalty ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of FISDataCategory from another instance of FISDataCategory.
     * @param other The FISDataCategory you want to clone
     * @returns A cloned FISDataCategory
     */
    public static FromOther(other: FISDataCategory): FISDataCategory {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return FISDataCategory._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return FISDataCategory.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'Codex' | 'Finalecodex' | 'Fiskategori' | 'Fistype' | 'Fvalue' | 'Minpenalty')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'FISDataCategory';
}
